"use client";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CustomModal from "./CustomModal";
import Icon from "../icons/Icons";
import { validatePhone, validateEmail, validateName } from "@/utils/validation";
import { usePathname } from "next/navigation";
import { apiPost } from "@/utils/apiClient";
import Link from "next/link";
import { ROOT_URL } from "@/utils/gloableVariables";

export const validatePAN = (pan: string): boolean => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan);

type HelpModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default function CheckKYCModal({ isOpen, onClose }: HelpModalProps) {
  const [step, setStep] = useState<1 | 2 | "result">(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<null | "success" | "failure">(null);

  const [pan, setPan] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const pathname = usePathname();

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setPan("");
      setName("");
      setPhone("");
      setEmail("");
      setErrors({});
      setLoading(false);
      setResult(null);
    }
  }, [isOpen]);

  const handleBlur = (field: string, value: string) => {
    let error = "";
    if (!value.trim()) {
      error = `Enter ${field}`;
    } else {
      switch (field) {
        case "PAN":
          if (!validatePAN(value)) error = "Enter a valid PAN Number";
          break;
        case "Name":
          if (!validateName(value)) error = "Enter a valid Name";
          break;
        case "Phone":
          if (!validatePhone(value)) error = "Enter a valid Phone Number";
          break;
        case "Email":
          if (!validateEmail(value)) error = "Enter a valid Email Address";
          break;
      }
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  // PAN input restrictions
  const handlePanChange = (val: string) => {
    let formatted = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (formatted.length > 10) formatted = formatted.slice(0, 10);
    setPan(formatted);
    setErrors((prev) => ({ ...prev, PAN: "" }));
  };

  // Phone input restrictions
  const handlePhoneChange = (val: string) => {
    let formatted = val.replace(/\D/g, "");
    if (formatted.length > 10) formatted = formatted.slice(0, 10);
    setPhone(formatted);
    setErrors((prev) => ({ ...prev, Phone: "" }));
  };

  // Name restrictions
  const handleNameChange = (val: string) => {
    let formatted = val.replace(/\s{2,}/g, " ");
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1); // capitalize first
    setName(formatted);
    setErrors((prev) => ({ ...prev, Name: "" }));
  };

  // Submit / Next
  const handleNext = async () => {
    const stepErrors: Record<string, string> = {};
    if (step === 1) {
      if (!validatePAN(pan)) stepErrors.PAN = "Enter a valid PAN Number";
      if (!validateName(name)) stepErrors.Name = "Enter a valid Name";
      if (Object.keys(stepErrors).length === 0) {
        setStep(2);
      }
    } else if (step === 2) {
      if (!validatePhone(phone)) stepErrors.Phone = "Enter a valid Phone Number";
      if (!validateEmail(email)) stepErrors.Email = "Enter a valid Email Address";
      if (Object.keys(stepErrors).length === 0) {
        const payload = { pan, name, phone, email, pathname };
        setLoading(true);
        try {
          const data = await apiPost("web/kycCheckWeb", payload);

          if (data?.status === "success") {
            setResult("success");
          } else {
            setResult("failure");
          }
          setStep("result");
        } catch (err) {
          setResult("failure");
          setStep("result");
          console.error(err || "Something Went Wrong")
        } finally {
          setLoading(false);
        }
      }
    }
    setErrors(stepErrors);
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      maxSize="max-w-[434px]"
      isBlurOverlay
      className="bg-[#2e6dd4]! rounded-2xl p-10 pt-5"
    >
      <div className="text-white">
        <p className="flex items-center gap-2.5 text-base sm:text-lg font-semibold text-[#ffc727]">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_8865_2117)">
              <path
                d="M2.126 15.7321C1.66978 12.7666 1.21355 9.80117 0.757327 6.83567C0.656152 6.1783 1.40413 5.72995 1.93618 6.12902C3.35758 7.19507 4.7789 8.26105 6.2003 9.3271C6.6683 9.6781 7.33505 9.56387 7.6595 9.07712L11.2095 3.75212C11.5846 3.1894 12.4114 3.1894 12.7866 3.75212L16.3366 9.07712C16.661 9.56387 17.3278 9.67802 17.7958 9.3271C19.2172 8.26105 20.6385 7.19507 22.0599 6.12902C22.5919 5.72995 23.3399 6.1783 23.2388 6.83567C22.7826 9.80117 22.3264 12.7666 21.8701 15.7321H2.126Z"
                fill="#FFB743"
              />
              <path
                d="M20.8416 20.6676H3.15633C2.58783 20.6676 2.12695 20.2067 2.12695 19.6382V17.377H21.8711V19.6382C21.871 20.2067 21.4101 20.6676 20.8416 20.6676Z"
                fill="#FFB743"
              />
            </g>
            <defs>
              <clipPath id="clip0_8865_2117">
                <rect width="24" height="24" fill="white" transform="translate(-0.00195312)" />
              </clipPath>
            </defs>
          </svg>
          1 Lac Users Opted for this Service
        </p>
        <div className="mt-2 text-3xl font-bold">Free KYC Check</div>
        <p className="mt-1.5 text-sm sm:text-2sm leading-5">To know your eligibility for Mutual Fund Investment</p>
      </div>

      <div className="py-7 px-4.5 bg-white rounded-xl mt-9 overflow-hidden">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
            >
              {/* PAN */}
              <div className="mb-5 relative text-[#222]">
                <label className="mb-3 block text-sm sm:text-base font-medium">PAN Card No.</label>
                <div className="flex items-center gap-3 rounded-xl border border-[#aaa] px-3 py-4 text-sm">
                  <Icon.cardIconV1 className="shrink-0" />
                  <input
                    type="text"
                    value={pan}
                    onChange={(e) => handlePanChange(e.target.value)}
                    onBlur={() => handleBlur("PAN", pan)}
                    placeholder="Enter 10 digit PAN Card No."
                    className="w-full placeholder:text-[#222] uppercase"
                  />
                </div>
                {errors.PAN && <p className="text-[#d50000] text-xs absolute -bottom-4.5">{errors.PAN}</p>}
              </div>

              {/* Name */}
              <div className="mb-5 relative text-[#222]">
                <label className="mb-3 block text-sm sm:text-base font-medium">Your name</label>
                <div className="flex items-center gap-3 rounded-xl border border-[#aaa] px-3 py-4 text-sm">
                  <Icon.userProfileIconV1 className="shrink-0" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    onBlur={() => handleBlur("Name", name)}
                    placeholder="Enter name as per PAN Card"
                    className="w-full placeholder:text-[#222]"
                  />
                </div>
                {errors.Name && <p className="text-[#d50000] text-xs absolute -bottom-4.5">{errors.Name}</p>}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
            >
              {/* Phone */}
              <div className="mb-5 relative text-[#222]">
                <label className="mb-3 block text-sm sm:text-base font-medium">Mobile No.</label>
                <div className="flex items-center gap-3 rounded-xl border border-[#aaa] px-3 py-4 text-sm">
                  <Icon.phoneIconV1 className="shrink-0" />
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    onBlur={() => handleBlur("Phone", phone)}
                    placeholder="Enter 10 digit Mobile Number"
                    className="w-full placeholder:text-[#222]"
                  />
                </div>
                {errors.Phone && <p className="text-[#d50000] text-xs absolute -bottom-4.5">{errors.Phone}</p>}
              </div>

              {/* Email */}
              <div className="mb-5 relative text-[#222]">
                <label className="mb-3 block text-sm sm:text-base font-medium">Email Address</label>
                <div className="flex items-center gap-3 rounded-xl border border-[#aaa] px-3 py-4 text-sm">
                  <Icon.emailIconV1 className="shrink-0" />
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors((prev) => ({ ...prev, Email: "" }));
                    }}
                    onBlur={() => handleBlur("Email", email)}
                    placeholder="Enter your Email Address"
                    className="w-full placeholder:text-[#222]"
                  />
                </div>
                {errors.Email && <p className="text-[#d50000] text-xs absolute -bottom-4.5">{errors.Email}</p>}
              </div>
            </motion.div>
          )}

          {step === "result" && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="text-center py-10"
            >
              {loading ? (
                <p className="text-[#222]">Checking KYC...</p>
              ) : result === "success" ? (
                <>
                  {/* <Icon.successIcon className="mx-auto w-12 h-12 text-green-600" /> */}
                  <p className="mt-4 text-lg font-semibold text-green-600">KYC Completed Successfully 🎉</p>
                </>
              ) : (
                <>
                  {/* <Icon.failedIcon className="mx-auto w-12 h-12 text-red-600" /> */}
                  <p className="mt-4 text-lg font-semibold text-red-600">KYC Failed. Please try again.</p>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Button (hide on result) */}
        {step !== "result" && (
          <button
            type="button"
            onClick={handleNext}
            disabled={loading}
            className="block w-fit mx-auto rounded-full bg-primaryBlue py-3 px-16 mb-1.5 text-white transition shine-button-hover disabled:opacity-60"
          >
            {step === 1 ? "Next" : loading ? "Submitting..." : "Submit"}
          </button>
        )}

        <p
          className={`flex items-center justify-center gap-2 text-xs sm:text-sm ${step === 1 ? "text-[#666]" : "text-primaryBlack"}`}
        >
          {step === 1 && <Icon.lockIconV1 className="w-4 h-4 shrink-0" />}
          {step === 1 ? "Your personal information is secure with us" : "By Proceeding, you agree"}
          {step === 2 && (
            <Link
              href={`${ROOT_URL}terms-conditions`}
              className="text-primaryBlue"
              aria-label="Terms and condtion Link"
            >
              T&C
            </Link>
          )}
        </p>
      </div>
    </CustomModal>
  );
}
